// The printer offset. ONE_PRINT_WORDS.md, approved by Robert 2026-10-24 (8:57 AM to 12:54 PM).
//
// The roll floats in the Toro Max, so the print can land off the border. The offset moves the whole
// print on the stock. The label's own drawing never changes, so the arc flash pixel test still holds.
//   * Kept on this iPad, not the site (8:57). One printer travels between sites.
//   * Three rolls: QR labels and QEW tags share one (9:10, same 2.25 × 1.0 in stock), field labels, arc flash.
//   * Each tap moves 0.25 mm, up to 3 mm either way. A typed value takes steps of 0.05 mm (12:54).
//   * Reset To Start puts back where each started: QR and QEW 2.15 mm up and 0.65 mm left (centered on the tape,
//     measured from Robert's GP 1 x 3 prints 2026-10-30 11:23 AM and 12:13 PM), the rest 0.
//   * Print Test Label prints one page with a border at the stock edge. It never counts as printed.

import { PDFDocument, rgb, StandardFonts, type PDFPage } from "pdf-lib";
import { driverPage, leadPt } from "./driver-page";

export type PrintRoll = "qrRoll" | "fieldRoll" | "arcFlashRoll";

/** Millimeters. Positive across moves the print right. Positive down moves it down. */
export interface PrinterOffset {
  acrossMm: number;
  downMm: number;
}

export const STEP_MM = 0.25;
export const FINE_MM = 0.05;
export const LIMIT_MM = 3;

/**
 * Where each roll starts. QR labels and QEW tags carried a fixed 0.5 mm lift (the .py Y_NUDGE). Robert's GP 1 x 3
 * print (2026-10-30 11:23 AM) measured 1.5 mm tape below the border and 3.05 mm above, 10.1 mm before it along the
 * tape and 8.7 mm after. Centered: 0.8 mm more up, 0.65 mm left. The 12:13 PM print at 1.30 mm up still sat low:
 * 2.2 mm above the border, 0.47 mm below (Robert: "maybe 1mm"). 0.85 mm more up. End to end it read 10.1 and 9.6 mm,
 * close enough to leave. Printer Setup still nudges from here.
 */
export const START_OFFSET: Readonly<Record<PrintRoll, PrinterOffset>> = {
  qrRoll: { acrossMm: -0.65, downMm: -2.15 },
  fieldRoll: { acrossMm: 0, downMm: 0 },
  arcFlashRoll: { acrossMm: 0, downMm: 0 },
};

export const ROLL_WORDS: Readonly<Record<PrintRoll, string>> = {
  qrRoll: "QR labels and QEW tags",
  fieldRoll: "field labels",
  arcFlashRoll: "arc flash labels",
};

/** Rounds to 0.05 mm and holds it inside ±3 mm. Not a number reads as 0. */
export function cleanMm(v: number): number {
  if (!Number.isFinite(v)) return 0;
  const held = Math.max(-LIMIT_MM, Math.min(LIMIT_MM, v));
  const r = Math.round(held / FINE_MM) * FINE_MM;
  return Math.round(r * 100) / 100 || 0;
}

export function cleanOffset(o: Partial<PrinterOffset> | null | undefined, roll: PrintRoll): PrinterOffset {
  if (!o || typeof o !== "object") return { ...START_OFFSET[roll] };
  return { acrossMm: cleanMm(Number(o.acrossMm)), downMm: cleanMm(Number(o.downMm)) };
}

/** One tap: Left or Right on across, Up or Down on down. */
export function nudge(o: PrinterOffset, axis: "across" | "down", dir: 1 | -1): PrinterOffset {
  return axis === "across" ? { ...o, acrossMm: cleanMm(o.acrossMm + dir * STEP_MM) } : { ...o, downMm: cleanMm(o.downMm + dir * STEP_MM) };
}

/** A typed value. "1.7", "-0.3", " 0.25 mm". Null when it isn't a number. */
export function typedMm(text: string): number | null {
  const v = parseFloat(String(text).replace(/mm/i, "").trim());
  return Number.isFinite(v) ? cleanMm(v) : null;
}

/** "0.50 mm up, 0.25 mm right". "No offset" at 0 and 0. */
export function offsetWords(o: PrinterOffset): string {
  const parts: string[] = [];
  if (o.downMm) parts.push(`${Math.abs(o.downMm).toFixed(2)} mm ${o.downMm > 0 ? "down" : "up"}`);
  if (o.acrossMm) parts.push(`${Math.abs(o.acrossMm).toFixed(2)} mm ${o.acrossMm > 0 ? "right" : "left"}`);
  return parts.length ? parts.join(", ") : "No offset";
}

/** The word beside one value on the card: "up", "down", "left", "right", or "" at 0. */
export function axisWord(axis: "across" | "down", mm: number): string {
  if (!mm) return "";
  return axis === "across" ? (mm > 0 ? "right" : "left") : mm > 0 ? "down" : "up";
}

// ---- Kept on this iPad ----

const KEY = "safetag.printerOffset.v1";

interface Store {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

function store(): Store | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readAll(s: Store | null): Partial<Record<PrintRoll, PrinterOffset>> {
  if (!s) return {};
  try {
    const v = JSON.parse(s.getItem(KEY) ?? "{}") as unknown;
    return v && typeof v === "object" ? (v as Partial<Record<PrintRoll, PrinterOffset>>) : {};
  } catch {
    return {};
  }
}

/** This iPad's offset for a roll. The start value when nothing is saved or storage is blocked. */
export function readOffset(roll: PrintRoll, s: Store | null = store()): PrinterOffset {
  const all = readAll(s);
  return all[roll] ? cleanOffset(all[roll], roll) : { ...START_OFFSET[roll] };
}

/** Saves the offset for a roll on this iPad. Returns what was saved. Storage trouble never stops a print. */
export function saveOffset(roll: PrintRoll, o: PrinterOffset, s: Store | null = store()): PrinterOffset {
  const clean = cleanOffset(o, roll);
  if (!s) return clean;
  try {
    s.setItem(KEY, JSON.stringify({ ...readAll(s), [roll]: clean }));
  } catch {
    /* private window or full storage: the print still uses the value for this session */
  }
  return clean;
}

export function resetOffset(roll: PrintRoll, s: Store | null = store()): PrinterOffset {
  return saveOffset(roll, START_OFFSET[roll], s);
}

// ---- On the page ----

const PT_PER_MM = 72 / 25.4;

/** Moves everything drawn on the page. A page turned 180° moves the other way, so the print still goes where the words say. */
export function shiftPage(page: PDFPage, o: PrinterOffset, turned180 = false): void {
  if (!o.acrossMm && !o.downMm) return;
  const sign = turned180 ? -1 : 1;
  page.translateContent(sign * o.acrossMm * PT_PER_MM, sign * -o.downMm * PT_PER_MM);
}

/**
 * One test page: a thin border at the label edge, moved by the offset, and the offset in words. Never a real label.
 * The page is the driver's (driver-page.ts); the label sits centered on it, as the real labels do.
 */
export async function testLabelPdf(widthIn: number, heightIn: number, o: PrinterOffset, what: string): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle("SafeTag Printer Test Label");
  pdf.setCreator("SafeTag Print");
  const font = await pdf.embedFont(StandardFonts.HelveticaBold);
  const drv = driverPage(heightIn, widthIn);
  const lead = leadPt(widthIn, drv);
  const PW = drv.length * 72;
  const W = widthIn * 72;
  const H = heightIn * 72;
  const page = pdf.addPage([PW, H]);
  const inset = 1.5;
  page.drawRectangle({ x: lead + inset, y: inset, width: W - inset * 2, height: H - inset * 2, borderColor: rgb(0, 0, 0), borderWidth: 1.5 });
  const lines = ["TEST LABEL", what, offsetWords(o)];
  const size = Math.max(5, Math.min(11, H / 7));
  lines.forEach((t, i) => {
    const s = i === 0 ? size : size * 0.8;
    const w = font.widthOfTextAtSize(t, s);
    page.drawText(t, { x: lead + (W - w) / 2, y: H / 2 + size * 1.1 - i * size * 1.3, size: s, font, color: rgb(0, 0, 0) });
  });
  shiftPage(page, o);
  return pdf.save();
}
